<template>
  <section ref="sectionRef" class="cta">
    <div class="cta__transition-in"></div>
    <div ref="atmosphereRef" class="cta__atmosphere"></div>
    <div class="cta__volumetric"></div>

    <div class="cta__inner">
      <blockquote ref="quoteRef" class="cta__quote">
        "O futuro não é um lugar para onde estamos indo. É um lugar que estamos
        construindo."
      </blockquote>

      <h2 ref="headingRef" class="cta__heading">Vamos Construir o Futuro?</h2>

      <p ref="descRef" class="cta__desc">
        Para falar sobre seu projeto, entre em contato diretamente conosco.
      </p>

      <div ref="linksRef" class="cta__links">
        <a
          href="mailto:contato@odigitalizando.com"
          class="cta__link"
          data-magnetic
        >
          <svg
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="1.5"
            stroke-linecap="round"
            stroke-linejoin="round"
          >
            <rect x="2" y="4" width="20" height="16" rx="2" />
            <path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7" />
          </svg>
          contato@odigitalizando.com
        </a>
        <a
          href="https://www.instagram.com/odigitalizando/"
          target="_blank"
          rel="noopener"
          class="cta__link"
          data-magnetic
        >
          <svg
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="1.5"
            stroke-linecap="round"
            stroke-linejoin="round"
          >
            <rect x="2" y="2" width="20" height="20" rx="5" />
            <circle cx="12" cy="12" r="5" />
            <circle
              cx="17.5"
              cy="6.5"
              r="1.5"
              fill="currentColor"
              stroke="none"
            />
          </svg>
          @odigitalizando
        </a>
      </div>

      <p ref="closerRef" class="cta__closer">
        Estamos ansiosos para ouvir sobre seu próximo desafio.
      </p>
    </div>

    <div class="cta__transition-out"></div>
  </section>
</template>

<script setup>
import { ref, onMounted, onUnmounted } from "vue";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

gsap.registerPlugin(ScrollTrigger);

const sectionRef = ref(null);
const atmosphereRef = ref(null);
const quoteRef = ref(null);
const headingRef = ref(null);
const descRef = ref(null);
const linksRef = ref(null);
const closerRef = ref(null);

let ctx;

onMounted(() => {
  ctx = gsap.context(() => {
    // Atmosphere parallax
    gsap.to(atmosphereRef.value, {
      y: 50,
      ease: "none",
      scrollTrigger: {
        trigger: sectionRef.value,
        start: "top bottom",
        end: "bottom top",
        scrub: 1,
      },
    });

    // ── QUOTE: violent fade ──
    gsap.fromTo(
      quoteRef.value,
      {
        autoAlpha: 0,
        y: 120,
        scale: 0.7,
        filter: "blur(25px)",
        rotationX: -45,
        transformPerspective: 1200,
      },
      {
        autoAlpha: 1,
        y: 0,
        scale: 1,
        filter: "blur(0px)",
        rotationX: 0,
        duration: 1.8,
        ease: "expo.out",
        scrollTrigger: {
          trigger: quoteRef.value,
          start: "top 90%",
          toggleActions: "play none none reverse",
        },
      }
    );

    // ── HEADING: slam into view ──
    gsap.fromTo(
      headingRef.value,
      {
        autoAlpha: 0,
        y: 100,
        scale: 0.5,
        filter: "blur(30px)",
        letterSpacing: "0.3em",
      },
      {
        autoAlpha: 1,
        y: 0,
        scale: 1,
        filter: "blur(0px)",
        letterSpacing: "0em",
        duration: 1.6,
        ease: "expo.out",
        scrollTrigger: {
          trigger: headingRef.value,
          start: "top 88%",
          toggleActions: "play none none reverse",
        },
      }
    );

    // ── DESC: rise from nothing ──
    gsap.fromTo(
      descRef.value,
      {
        autoAlpha: 0,
        y: 80,
        filter: "blur(20px)",
        clipPath: "inset(100% 0% 0% 0%)",
      },
      {
        autoAlpha: 1,
        y: 0,
        filter: "blur(0px)",
        clipPath: "inset(0% 0% 0% 0%)",
        duration: 1.4,
        delay: 0.15,
        ease: "power4.out",
        scrollTrigger: {
          trigger: descRef.value,
          start: "top 90%",
          toggleActions: "play none none reverse",
        },
      }
    );

    // ── LINKS: stagger with heavy blur + scale ──
    const linkEls = linksRef.value?.querySelectorAll(".cta__link");
    if (linkEls && linkEls.length) {
      gsap.fromTo(
        linkEls,
        {
          autoAlpha: 0,
          y: 60,
          scale: 0.6,
          filter: "blur(18px)",
        },
        {
          autoAlpha: 1,
          y: 0,
          scale: 1,
          filter: "blur(0px)",
          duration: 1.2,
          stagger: 0.25,
          ease: "back.out(1.4)",
          scrollTrigger: {
            trigger: linksRef.value,
            start: "top 90%",
            toggleActions: "play none none reverse",
          },
        }
      );
    }

    // ── CLOSER: ghost fade ──
    gsap.fromTo(
      closerRef.value,
      {
        autoAlpha: 0,
        y: 50,
        filter: "blur(15px)",
        scale: 0.85,
      },
      {
        autoAlpha: 1,
        y: 0,
        filter: "blur(0px)",
        scale: 1,
        duration: 1.6,
        delay: 0.4,
        ease: "expo.out",
        scrollTrigger: {
          trigger: closerRef.value,
          start: "top 92%",
          toggleActions: "play none none reverse",
        },
      }
    );
  }, sectionRef.value);
});

onUnmounted(() => {
  if (ctx) ctx.revert();
});
</script>

<style scoped>
.cta {
  position: relative;
  min-height: 100vh;
  padding: 8rem 2rem;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
}

.cta__transition-in {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 200px;
  background: linear-gradient(to bottom, var(--color-bg-deep), transparent);
  z-index: 4;
  pointer-events: none;
}

.cta__transition-out {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  height: 200px;
  background: linear-gradient(to bottom, transparent, var(--color-bg-deep));
  z-index: 4;
  pointer-events: none;
}

.cta__atmosphere {
  position: absolute;
  inset: -30%;
  background: radial-gradient(
      ellipse at 50% 40%,
      rgba(251, 191, 36, 0.04) 0%,
      transparent 40%
    ),
    radial-gradient(
      ellipse at 30% 70%,
      rgba(167, 139, 250, 0.03) 0%,
      transparent 50%
    );
  z-index: 0;
  animation: ctaDrift 22s ease-in-out infinite alternate;
  will-change: transform;
}

.cta__volumetric {
  position: absolute;
  top: 35%;
  left: 50%;
  width: 500px;
  height: 300px;
  transform: translateX(-50%);
  background: radial-gradient(
    ellipse,
    rgba(251, 191, 36, 0.06) 0%,
    rgba(167, 139, 250, 0.03) 50%,
    transparent 70%
  );
  filter: blur(100px);
  mix-blend-mode: color-dodge;
  z-index: 1;
  pointer-events: none;
}

@keyframes ctaDrift {
  0% {
    transform: translate(0, 0) scale(1);
  }
  100% {
    transform: translate(-15px, 10px) scale(1.04);
  }
}

.cta__inner {
  position: relative;
  z-index: 5;
  max-width: 700px;
  text-align: center;
  pointer-events: auto;
}

.cta__quote {
  font-family: var(--font-display);
  font-size: clamp(1.2rem, 2.5vw, 1.6rem);
  font-weight: 400;
  font-style: italic;
  color: var(--color-text-secondary);
  line-height: 1.6;
  margin-bottom: 3rem;
  visibility: hidden;
  transform-origin: center bottom;
  will-change: transform, opacity, filter;
}

.cta__heading {
  font-size: clamp(2rem, 5vw, 3.5rem);
  color: var(--color-text-primary);
  margin-bottom: 1.5rem;
  visibility: hidden;
  position: relative;
  will-change: transform, opacity, filter;
}

.cta__heading::after {
  content: "";
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 300px;
  height: 100px;
  background: radial-gradient(
    ellipse,
    rgba(251, 191, 36, 0.08) 0%,
    transparent 70%
  );
  filter: blur(40px);
  mix-blend-mode: color-dodge;
  pointer-events: none;
  z-index: -1;
}

.cta__desc {
  font-size: 1rem;
  color: var(--color-text-secondary);
  margin-bottom: 2.5rem;
  line-height: 1.7;
  visibility: hidden;
  will-change: transform, opacity, filter, clip-path;
}

.cta__links {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1rem;
  margin-bottom: 3rem;
}

.cta__link {
  display: inline-flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.85rem 1.8rem;
  background: var(--color-bg-glass);
  border: 1px solid var(--color-border-glass);
  border-radius: 50px;
  font-size: 0.95rem;
  color: var(--color-text-primary);
  backdrop-filter: blur(24px);
  -webkit-backdrop-filter: blur(24px);
  visibility: hidden;
  will-change: transform, opacity, filter;
  transition: border-color 0.3s var(--ease-out-quart),
    box-shadow 0.3s var(--ease-out-quart), color 0.3s var(--ease-out-quart),
    transform 0.3s var(--ease-out-quart);
}

.cta__link:hover {
  border-color: var(--color-accent);
  box-shadow: 0 0 30px var(--color-accent-glow),
    0 0 60px rgba(167, 139, 250, 0.08);
  color: var(--color-accent);
  transform: translateY(-2px);
}

.cta__closer {
  font-size: 0.9rem;
  color: var(--color-text-muted);
  visibility: hidden;
  will-change: transform, opacity, filter;
}

@media (max-width: 768px) {
  .cta {
    padding: 5rem 1.5rem;
  }
}
</style>
